"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Box } from "~/components/Box/Box";
import { SearchCategoryBar } from "~/components/CategoryBar/SearchCategoryBar";
import { ExclusiveDealConflictDialog } from "~/components/ExclusiveDealConflictDialog/ExclusiveDealConflictDialog";
import {
  HOT_DEAL_CARD_HEIGHT,
  HotDealCard,
  HotDealCardSkeleton,
} from "~/components/HotDealCard/HotDealCard";
import {
  MenuItemCard,
  MenuItemCardSkeleton,
} from "~/components/MenuItemCard/MenuItemCard";
import { MobileCheckoutButton } from "~/components/MobileCheckoutButton/MobileCheckoutButton";
import { NoMatch } from "~/components/NoMatch/NoMatch";
import { NoMenu } from "~/components/NoMenu/NoMenu";
import { PageWrapper } from "~/components/PageWrapper/PageWrapper";
import { Stack } from "~/components/Stack/Stack";
import { Text } from "~/components/Text/Text";
import { Tiles } from "~/components/Tiles/Tiles";
import {
  HOT_DEALS_CATEGORY_ID_PREFIX,
  PAGE_MAX_WIDTH,
  PAGE_TOPSET,
  VIEWPORT_MD,
} from "~/constants";
import type { LocationDeal } from "~/graphql-client/generated";
import { useActiveAnchor } from "~/hooks/active-anchor";
import { useCart } from "~/hooks/cart";
import { useDealConflict, useLocationDeals } from "~/hooks/deals";
import useDebounce from "~/hooks/debounce";
import { useLocation } from "~/hooks/locations";
import { useBaseMenu } from "~/hooks/menu-v2";
import { useWindowDimensions } from "~/hooks/window-width";
import { ChevronDownIcon, ChevronUpIcon } from "~/icons";
import type {
  OrderDispatchOption,
  Product,
  ProductCategory,
  TimeSlot,
} from "~/types";
import { getCategoryDomId } from "~/utils/category";
import {
  dayOfWeek,
  getConflictingDeals,
  productVisibleToday,
} from "~/utils/deal";

export default function MenuPage() {
  const { locationRef, location } = useLocation();

  const router = useRouter();

  if (!location) throw new Error("Location not found");

  const { dispatchOption, timeSlot, setOrderingOptions } = useCart(locationRef);

  const { menu, isLoading: loadingMenu } = useBaseMenu(
    dispatchOption,
    timeSlot,
  );

  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValue);

  const filteredMenu: ProductCategory[] | null = filterMenu(
    menu,
    debouncedSearchValue,
  );

  const catIds = filteredMenu ? filteredMenu.map((c) => c.id) : [];
  const currentCategoryId =
    useActiveAnchor(catIds, PAGE_TOPSET + 1) || catIds[0];

  const hotDeals = filteredMenu?.find((c) =>
    c.id.startsWith(HOT_DEALS_CATEGORY_ID_PREFIX),
  );

  // Legacy deals from the Featured Hot Deals category, visible today and not hidden.
  const currentDay = dayOfWeek(location?.timezone);
  const legacyDeals: Product[] = (hotDeals?.products ?? []).filter(
    (p) =>
      productVisibleToday(p, currentDay) &&
      !p.tags.includes("ui-hint:product:hidden"),
  );

  const mainMenu = filteredMenu?.filter(
    (c) => !c.id.startsWith(HOT_DEALS_CATEGORY_ID_PREFIX),
  );

  const maxWidth = PAGE_MAX_WIDTH;

  useEffect(() => {
    if (debouncedSearchValue)
      window?.scrollTo({
        top: 0,
      });
  }, [debouncedSearchValue]);

  useOrderingOptionsFromQuery(setOrderingOptions);

  const { deals, loading: loadingDeals } = useLocationDeals(
    locationRef,
    dispatchOption,
  );

  return (
    <>
      <PageWrapper>
        {/* Don't show search bar when no menu available */}
        {!loadingMenu && !menu.length ? null : (
          <SearchCategoryBar
            currentCategoryId={currentCategoryId}
            filteredMenu={filteredMenu || []}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            loading={loadingMenu}
          />
        )}
        {/* Anchor for the category bar's "Featured Hot Deals" chip — always present
            when the category exists, even while deals load or none are available. */}
        {hotDeals && <div id={getCategoryDomId(hotDeals.id)} />}
        {!loadingDeals &&
          (deals.some((d) => !d.hidden) || legacyDeals.length > 0) && (
            <Box background="jets-beige" paddingX={["3", "3", "5"]}>
              <Box paddingBottom="4" style={{ maxWidth, margin: "auto" }}>
                <HotDealsSection
                  locationRef={locationRef}
                  deals={deals || []}
                  legacyDeals={legacyDeals}
                />
              </Box>
            </Box>
          )}
        <Box
          paddingX={["3", "3", "5"]}
          display="flex"
          flexDirection="column"
          flexGrow={1}
        >
          {!menu?.length && !loadingMenu ? <NoMenu /> : null}
          {Boolean(debouncedSearchValue && !filteredMenu?.length) && (
            <NoMatch />
          )}
          {mainMenu && (
            <MenuGrid
              menu={mainMenu}
              locationRef={locationRef}
              loading={loadingMenu}
              maxWidth={maxWidth}
            />
          )}
        </Box>
      </PageWrapper>
      <MobileCheckoutButton
        onPress={() => router.push(`/${locationRef}/cart`)}
        locationRef={locationRef}
      />
    </>
  );
}

function MenuGrid({
  menu,
  locationRef,
  loading,
  maxWidth,
}: {
  menu: ProductCategory[];
  locationRef: string;
  loading: boolean;
  maxWidth: number;
}) {
  const router = useRouter();

  const columns = [2, 3, 3] as const;

  if (loading) {
    const hotDeals = [1, 2, 3];
    const cards = [1, 2, 3, 4, 5, 6, 7, 8];
    return (
      <Box>
        <Box style={{ maxWidth, margin: "auto" }} paddingY={"3"}>
          <Stack space="3">
            <Box>
              <Text font="accent" size="xxlarge">
                <Skeleton width={240} />
              </Text>
            </Box>

            <Tiles space="2" columns={columns}>
              {hotDeals.map((i) => (
                <HotDealCardSkeleton key={i} />
              ))}
            </Tiles>
            <Text font="accent" size="xxlarge">
              <Skeleton width={240} />
            </Text>
            <Tiles space="2" columns={columns}>
              {cards.map((i) => (
                <MenuItemCardSkeleton key={i} />
              ))}
            </Tiles>
          </Stack>
        </Box>
      </Box>
    );
  }

  if (!menu) return null;

  return (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="center"
      style={{ paddingBottom: 25 }}
    >
      <Box style={{ maxWidth }} flexGrow={1}>
        {menu.map((category: ProductCategory) => {
          return (
            <div key={category.id}>
              <div id={getCategoryDomId(category.id)} />
              <Box paddingY={"3"}>
                <Stack space="3">
                  <Box>
                    <Text
                      font="accent"
                      size="xxlarge"
                      transform="uppercase"
                      weight="strong"
                    >
                      {(category.title || "").trim()}
                    </Text>
                  </Box>

                  <Tiles space="3" columns={columns}>
                    {category.products?.map((item: Product) => {
                      return (
                        <MenuItemCard
                          key={item.id}
                          product={item}
                          onSelect={() => {
                            router.push(
                              `/${locationRef}/menu-item?productId=${encodeURIComponent(
                                item.id,
                              )}`,
                              {
                                scroll: false,
                              },
                            );
                          }}
                        />
                      );
                    })}
                  </Tiles>
                </Stack>
              </Box>
            </div>
          );
        })}
      </Box>
    </Box>
  );
}

function HotDealsSection(props: {
  locationRef: string;
  deals: LocationDeal[];
  legacyDeals: Product[];
}) {
  const { locationRef } = props;
  const router = useRouter();
  const { cart } = useCart(locationRef);

  // filter out the hidden deals, these are still accessible via hot deal code (plu)
  const newDeals = props.deals.filter((d) => !d.hidden);

  // The deal a customer selected that conflicts with the cart's deals
  const [conflictingDeal, setConflictingDeal] = useState<LocationDeal | null>(
    null,
  );

  const { dealsToRemove } = useDealConflict(
    cart.items,
    props.deals,
    conflictingDeal,
  );

  const navigateToDeal = (deal: LocationDeal) => {
    router.push(`/${locationRef}/deal?dealId=${encodeURIComponent(deal.id)}`, {
      scroll: false,
    });
  };

  const handleSelectDeal = (deal: LocationDeal) => {
    if (getConflictingDeals(cart.items, props.deals, deal).count >= 1) {
      setConflictingDeal(deal);
      return;
    }
    navigateToDeal(deal);
  };

  // Conflicting deals aren't removed here — addItem removes the conflicting deals once the user finishes configuring the new deal.
  // Abandoning configuration leaves the existing cart untouched.
  const handleReplaceDeal = () => {
    if (!conflictingDeal) return;

    const dealToOpen = conflictingDeal;
    setConflictingDeal(null);

    navigateToDeal(dealToOpen);
  };

  // Legacy deals route through the same /deal flow via productId.
  const navigateToLegacyDeal = (product: Product) => {
    router.push(
      `/${locationRef}/deal?productId=${encodeURIComponent(product.id)}`,
      { scroll: false },
    );
  };

  // Merged deals for display: new deals first, then legacy deals.
  const dealCards = [
    ...newDeals.map((deal) => ({
      key: `deal:${deal.id}`,
      title: deal.title,
      description: deal.description,
      // Always-enabled for now (UV-198).
      disabled: false,
      isNew: true,
      onSelect: () => handleSelectDeal(deal),
    })),
    ...props.legacyDeals.map((product) => ({
      key: `product:${product.id}`,
      title: product.title,
      description: product.description,
      disabled: !product.available,
      isNew: false,
      onSelect: () => navigateToLegacyDeal(product),
    })),
  ];

  /* Deal cards use a green dashed border, except when both old and new deals
     are shown together — then old (legacy) deals get a red border to set them apart. */
  const bothDealTypesShown =
    newDeals.length > 0 && props.legacyDeals.length > 0;

  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggle = () => setCollapsed(!collapsed);

  const columns = [2, 3, 3] as const;
  const { width } = useWindowDimensions();
  const columnsPerRow = width < VIEWPORT_MD ? 2 : 3;
  const rows = Math.ceil(dealCards.length / columnsPerRow);
  // 40 is added to HOT_DEAL_CARD_HEIGHT to account for padding between hot deal cards
  const sectionMaxHeight = (HOT_DEAL_CARD_HEIGHT + 40) * rows;

  return (
    <>
      <div id={"dealsHeading"} />
      <Box paddingY={"2"}>
        <Stack space="3">
          <Box>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Text
                font="accent"
                size="xxlarge"
                transform="uppercase"
                weight="strong"
              >
                Featured Hot Deals
              </Text>
            </Box>
          </Box>

          <Box
            style={{
              maxHeight: collapsed ? "180px" : `${sectionMaxHeight}px`,
              transition: "max-height linear 0.5s",
              overflow: "hidden",
            }}
            aria-expanded={!collapsed}
            id={"hot-deals"}
          >
            <Tiles space="3" columns={columns}>
              {dealCards.map((card, index) => {
                // Calculate if this item is in the visible rows when collapsed
                const row = Math.floor(index / columnsPerRow);
                const isVisibleWhenCollapsed = row < 1; // First row is visible

                // Only allow tabbing to visible items when collapsed
                const shouldBeTabFocusable =
                  !collapsed || isVisibleWhenCollapsed;

                return (
                  // TODO (UV-198): derive new deals' disabled state from availability instead of always-enabled.
                  <HotDealCard
                    key={card.key}
                    title={card.title}
                    description={card.description}
                    disabled={card.disabled}
                    borderColor={
                      !card.isNew && bothDealTypesShown ? "jets-red" : undefined
                    }
                    tabIndex={shouldBeTabFocusable ? 0 : -1}
                    onSelect={card.onSelect}
                  />
                );
              })}
            </Tiles>
          </Box>

          <Box
            display={"flex"}
            alignItems="center"
            justifyContent="center"
            flexDirection="row"
            width="full"
            marginTop={"2"}
            onClick={toggle}
            role="button"
            aria-expanded={!collapsed}
            aria-controls={`hot-deals`}
            aria-label={`${collapsed ? "Show more" : "Show less"} hot deals`}
          >
            <div>
              <Text
                weight="strong"
                size="xxlarge"
                font="special"
                color="jets-black"
              >
                {`SEE ${collapsed ? "MORE" : "LESS"} HOT DEALS`}
              </Text>
            </div>
            <div
              style={{
                marginLeft: 10,
                border: "2px solid var(--color-jets-black)",
                height: 42,
                width: 42,
                borderRadius: 100,
              }}
              aria-hidden="true"
            >
              <Text size="xxlarge" color="jets-black" weight="strong">
                {collapsed ? (
                  <ChevronDownIcon width={42} height={42} />
                ) : (
                  <ChevronUpIcon width={42} height={42} />
                )}
              </Text>
            </div>
          </Box>
        </Stack>
      </Box>
      <ExclusiveDealConflictDialog
        open={conflictingDeal !== null}
        newDeal={conflictingDeal}
        dealsToRemove={dealsToRemove}
        onKeepCurrent={() => setConflictingDeal(null)}
        onReplace={handleReplaceDeal}
      />
    </>
  );
}

function filterMenu(
  menu: ProductCategory[] | null,
  searchValue: string,
): ProductCategory[] | null {
  if (!menu) return null;

  return menu
    .map((category): ProductCategory => {
      const value = searchValue.toLocaleLowerCase();

      // Return entire category if category title includes the search value
      if (category.title.toLocaleLowerCase().includes(value)) return category;
      return {
        id: category.id,
        title: category.title,
        products: category.products?.filter((product) => {
          if (product.description?.toLocaleLowerCase().includes(value))
            return true;

          if (product.title.toLocaleLowerCase().includes(value)) return true;

          if (
            product.options?.some((option) => {
              if (option.title.toLocaleLowerCase().includes(value)) return true;

              return option.choices.some((choice) => {
                return choice.product.title.toLocaleLowerCase().includes(value);
              });
            })
          )
            return true;
        }),
      };
    })
    .filter((category) => category.products?.length);
}

//Note: duplicated helper function from menu-old.tsx
function getDispatchOptionFromQuery(str: string): OrderDispatchOption | null {
  const dispatchOption = str.toUpperCase();

  switch (dispatchOption) {
    case "DELIVERY":
    case "PICK_UP":
    case "SERVICE":
      return dispatchOption;
    case "PICKUP":
      return "PICK_UP";
    default:
      return null;
  }
}

// Pulls the dispatch type from the query string, setting the ordering option
// in redux via the handler function, and then removing the query param
function useOrderingOptionsFromQuery(
  handler: (
    dispatchOption: OrderDispatchOption,
    timeSlot: TimeSlot | null,
    tableRef: string,
  ) => void,
) {
  const query = useSearchParams();
  const fulfillment = query?.get("fulfillment");
  const tableRef = query?.get("ref") || "";

  const router = useRouter();

  useEffect(() => {
    // prompting user for their input overrides setting on URL
    if (!fulfillment) return;

    const dispatchOption = getDispatchOptionFromQuery(fulfillment);
    if (!dispatchOption) return;

    handler(dispatchOption, null, tableRef);

    router.replace(window.location.pathname, undefined);
  }, [handler, fulfillment, tableRef, router]);

  return fulfillment;
}
